import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import styled, { css } from 'styled-components';
import Tooltip from '@carvana/showroom-tooltip';
import ThemeProvider from '@carvana/showroom-theme';
import { PurchaseHeaderClock } from '@carvana/purchase-icons';

const TooltipTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: ${props => props.theme.carvana.white};
  line-height: 20px;
  font-family: Brandon Text;
`;

const TooltipContent = styled.span`
  font-size: 14px;
  color: ${props => props.theme.carvana.white};
  font-weight: 400;
  line-height: 20px;
  font-family: Brandon Text;
`;

const TimerDisplay = styled.div`
  display: flex;
  flex-direction: row;
  min-width: 60px;
`;

const Digit = styled.div`
  min-width: 2px;
`;

const TimerWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  ${props =>
    !props.isMobile &&
    css`
      border-radius: 61px;
      border: 1px solid #d9dde9;
      min-width: 108px;
    `}
  padding: ${props => (props.isMobile ? '8px 12px 8px 0px' : '8px 12px')};
  ${props => props.isMobile && 'width: auto;'}
  font-size: 14px;
  line-height: 16px;
  white-space: nowrap;
  font-family: Brandon Text;
`;

const formatNumberAsTime = (number: number) => {
  return String(number).padStart(2, '0');
};

const getTimerFormat = (expiresOn: string) => {
  const now = dayjs();
  const expires = dayjs(expiresOn);

  if (!expires.isValid()) return '00:00:00';

  if (expires.isBefore(now)) return '00:00:00';
  const hours = formatNumberAsTime(expires.diff(now, 'hour'));
  const minutes = formatNumberAsTime(expires.diff(now, 'minute') % 60);
  const seconds = formatNumberAsTime(
    Math.floor(expires.diff(now, 'second') % 60)
  );
  return `${hours}:${minutes}:${seconds}`;
};

const defaultProps = {
  display: true,
  vehicleLock: {
    isAvailable: true,
    expiresOn: dayjs().add(1, 'hour').toISOString()
  },
  isMobile: false,
  isModal: false,
  showCart: false
};

const LockTimer = (props = defaultProps) => {
  const { display, vehicleLock: lock, isMobile, isModal, showCart } = props;
  const [timerDisplay, setTimerDisplay] = useState(
    getTimerFormat(lock?.expiresOn)
  );

  useEffect(() => {
    const id = setTimeout(() => {
      const newTimerDisplay = getTimerFormat(lock.expiresOn);
      if (newTimerDisplay !== timerDisplay) setTimerDisplay(newTimerDisplay);
    }, 1000);

    return () => {
      clearTimeout(id);
    };
  }, [timerDisplay, lock.expiresOn]);

  if (!display || !lock.isAvailable) return null;

  const setTooltipDisplay = () => {
    const width = isModal ? 275 : 327;
    const height = 136;
    const contentOffsetX =
      isMobile && !isModal ? 70 : !showCart && !isModal ? -100 : 0;
    const arrowOffsetX =
      isMobile && !isModal ? -55 : !showCart && !isModal ? 115 : 15;

    return {
      width,
      height,
      contentOffsetX,
      arrowOffsetX
    };
  };

  return (
    <ThemeProvider>
      <Tooltip
        content={() => (
          <>
            <TooltipTitle>Vehicle is temporarily reserved</TooltipTitle>
            <TooltipContent>
              This vehicle is being held for you while you provide purchase
              details. When time runs out, the vehicle will unlock and could be
              purchased by someone else.
            </TooltipContent>
          </>
        )}
        display={setTooltipDisplay()}
        persist
      >
        <TimerWrapper isMobile={isMobile}>
          <PurchaseHeaderClock />
          <TimerDisplay>
            {timerDisplay.split('').map((digit, index) => (
              <Digit key={index}>{digit}</Digit>
            ))}
          </TimerDisplay>
        </TimerWrapper>
      </Tooltip>
    </ThemeProvider>
  );
};

export default LockTimer;
